/* eslint-disable @angular-eslint/no-input-rename */
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  ChangeDetectionStrategy,
  Input,
  EventEmitter,
  Output,
  NgZone,
  inject,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Params,
  Router,
  Event,
  RouterLink,
} from '@angular/router';
import { configureScope } from '@sentry/browser';
import { Subscription, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { UserModel } from '@app/shared/models/api/user.model';
import { MetaService } from '@app/shared/services/meta.service';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { WindowUtils } from '@app/shared/utils/window.util';
import {
  Validators,
  UntypedFormBuilder,
  UntypedFormGroup,
  UntypedFormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { AuthAPIService } from '@app/akita/api/auth/services/auth.service';
import { AkitaAuthService } from '@app/akita/api/auth/state/auth.service';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { CryptoUtils } from '@app/shared/utils/crypto.util';
import {
  logLogin,
  logSignupApple,
  logSignupGoogle,
} from '@app/core/services/google-analytics.lazy';
import { AkitaCartService } from '@app/akita/cart/state/cart.service';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { View1IconComponent } from '../../../shared/components/icons/generated/view-1/view-1.component';
import { ViewOffIconComponent } from '../../../shared/components/icons/generated/view-off/view-off.component';
import { RemoveIconComponent } from '../../../shared/components/icons/generated/remove/remove.component';
import { ArrowLeft1IconComponent } from '../../../shared/components/icons/generated/arrow-left-1/arrow-left-1.component';
import { NgIf, AsyncPipe } from '@angular/common';

const MIN_PASSWORD_LENGTH = 6;

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.template.html',
  styleUrls: ['./sign-in.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    NgIf,
    ArrowLeft1IconComponent,
    RemoveIconComponent,
    ReactiveFormsModule,
    ViewOffIconComponent,
    View1IconComponent,
    LoadingComponent,
    RouterLink,
    AsyncPipe,
  ],
})
export class SignInComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() public inDialog?: boolean | null = null;
  @Input('email') public placeholderEmail?: string | null = null;
  @Input('username') public placeholderUsername?: string | null = null;
  @Input('previous-mode') public previousMode?: string | null = null;
  @Input('success-path') public successPath?: Array<string> | null = null;
  @Input('success-params') public successRedirectQueryParams?: Params | null = null;
  @Input('skip-redirect') public skipRedirect?: boolean | null = null;

  @Output() public readonly handleRedirect: EventEmitter<void> = new EventEmitter();
  @Output() public readonly dialogModeChange: EventEmitter<any> = new EventEmitter();
  @Output() public readonly closeDialog: EventEmitter<boolean> = new EventEmitter();

  public emailFormControl: UntypedFormControl;
  public passwordFormControl: UntypedFormControl;
  public signInForm: UntypedFormGroup;
  public redirect?: string | null = null;
  public hidePassword: boolean;
  public submitted: boolean;
  public failed: boolean;
  public success: boolean;
  public loading: boolean;
  public isBrowser: boolean;
  public params: Params = {};
  public queryParams: Params = {};
  public routeData: Params = {};

  private readonly urlObserver: Subscription | null;
  private readonly langChangesObserver: Subscription | null;
  private sha256HasherObserver?: Subscription | null;
  private readonly userLoginObserver?: Subscription | null;
  private readonly subscriptions: Subscription;

  public lessThan$: Observable<ScreenSizeInfoState>;
  public isRtlLayout$: Observable<boolean>;

  private readonly destroySubject: Subject<void>;

  @ViewChild('email') private readonly emailElement?: ElementRef | null;
  @ViewChild('password') private readonly passwordElement?: ElementRef | null;

  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly translateService = inject(TranslocoService);

  constructor(
    private readonly meta: MetaService,
    private readonly authAPIService: AuthAPIService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly akitaAuthService: AkitaAuthService,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly akitaCartService: AkitaCartService
  ) {
    this.destroySubject = new Subject<void>();
    this.subscriptions = new Subscription();
    this.isBrowser = this.akitaRouterQuery.isBrowser;
    this.hidePassword = true;
    this.submitted = false;
    this.loading = false;
    this.success = false;
    this.failed = false;

    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.isRtlLayout$ = this.akitaRouterQuery.selectIsRtlLayout();

    this.emailFormControl = new UntypedFormControl('', {
      validators: Validators.compose([Validators.required, Validators.email]),
      updateOn: 'submit',
    });

    this.passwordFormControl = new UntypedFormControl('', {
      validators: Validators.compose([
        Validators.required,
        Validators.minLength(MIN_PASSWORD_LENGTH),
      ]),
      updateOn: 'submit',
    });

    this.signInForm = this.formBuilder.group({
      email: this.emailFormControl,
      password: this.passwordFormControl,
    });

    this.langChangesObserver = this.translateService.langChanges$
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: () => {
          const description = this.translateService.translate('app_title');
          this.meta.setTitle('title_login', true);
          this.meta.setCanonical();
          this.meta.setAlternateHreflangsTags(null);

          this.meta.setTags([
            { name: 'og:url', content: `${WindowUtils.getLocationHref() || ''}` },
            { name: 'og:type', content: `website` },
            { name: 'og:description', content: description },
            { name: 'description', content: description },
          ]);
        },
        error: () => {},
      });

    // Listen to Navigation End events
    this.urlObserver = this.router.events.pipe(takeUntil(this.destroySubject)).subscribe({
      next: ($event: Event) => {
        if ($event instanceof NavigationEnd) {
          let route = this.activatedRoute.snapshot;
          while (route.firstChild) {
            route = route.firstChild;
          }
          this.onNavigationEnd(
            route.params || {},
            route.queryParams || {},
            route.data || {}
          );
        }
      },
    });

    if (this.isBrowser) {
      this.akitaAuthService.initializeAppleAuthAsync();
    }
  }

  /**
   * Method called when the component is initialized
   */
  public ngOnInit(): void {
    let route = this.activatedRoute.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
    }
    this.onNavigationEnd(route.params || {}, route.queryParams || {}, route.data || {});
  }

  public ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          const email =
            this.params.email || this.queryParams.email || this.placeholderEmail || '';

          // Set Focus
          if (email) {
            if (this.passwordElement?.nativeElement?.focus) {
              this.passwordElement.nativeElement.focus();
            }
          } else {
            if (this.emailElement?.nativeElement?.focus) {
              this.emailElement.nativeElement.focus();
            }
          }
        }, 0);
      });
    }
  }

  /**
   * Method called when the view loads
   * @param params: URL Params
   * @param queryParams: URL Query Params
   * @param routeData: Route Data
   */
  public onNavigationEnd(params: Params, queryParams: Params, routeData?: Params): void {
    this.params = params || {};
    this.queryParams = queryParams || {};
    this.routeData = routeData || {};
    this.redirect = decodeURI(this.queryParams.redirect || this.params.redirect || '');
    this.changeDetector.markForCheck();

    const emailField = this.signInForm.get('email');
    const email =
      this.params.email || this.queryParams.email || this.placeholderEmail || '';
    if (emailField && email) {
      emailField.setValue(email);
      emailField.markAsTouched();
    }
  }

  /**
   * Method called when the Sign in (Popsy or Facebook) completes
   * @param user The user instance from the Backend
   */
  public loggedIn(user: UserModel, skipReportingToStore?: boolean | null): boolean {
    this.loading = false;
    if (user) {
      this.success = true;

      if (!skipReportingToStore) {
        this.akitaAuthService.userSignedIn(user);
      }
    } else {
      this.success = false;
    }
    this.changeDetector.markForCheck();

    if (this.redirect && user && user.oauth && user.oauth.token) {
      WindowUtils.setLocationHref(`${this.redirect}${user.oauth.token.accessToken}`);
      return false;
    } else {
      let params = {
        ...(this.successRedirectQueryParams || {}),
      };
      if (this.redirect) {
        params = { ...params, redirect: encodeURI(this.redirect) };
      }

      this.handleRedirect.next();
      if (!this.skipRedirect) {
        if (this.successPath && this.successPath.length > 0) {
          // Replace the ID in the fragment (since we did not know the ID until now)
          for (let i = 0; i < this.successPath.length; i += 1) {
            this.successPath[i] = this.successPath[i].replace(
              '--logged-user-id--',
              user.id
            );
          }

          this.router
            .navigate(this.successPath, {
              queryParams: params,
            })
            .catch((error) => {
              console.log(error);
            });
        } else {
          this.router
            .navigate(this.akitaRouterQuery.homeUrlFragment, {
              queryParams: params,
            })
            .catch((error) => {
              console.log(error);
            });
        }
      }
    }

    this.close(false);
    return true;
  }

  /**
   * Sign in with Popsy
   */
  public signIn(): void {
    if (this.signInForm.status !== 'VALID') {
      return;
    }

    const emailField = this.signInForm.get('email');
    let email = '';
    if (emailField) {
      email = emailField.value || '';
    }

    if (this.sha256HasherObserver) {
      this.sha256HasherObserver.unsubscribe();
    }

    const passwordField = this.signInForm.get('password');
    this.sha256HasherObserver = CryptoUtils.Sha256(passwordField?.value || '')
      .pipe(
        switchMap((password: string) => {
          this.loading = true;
          this.submitted = true;
          this.failed = false;
          this.success = false;

          configureScope((scope) => {
            scope.setUser({
              id: '?',
              username: '',
              email: email,
            });
          });

          return this.authAPIService.signIn(email, password);
        }),
        takeUntil(this.destroySubject)
      )
      .subscribe({
        next: (user: UserModel | null) => {
          logLogin(this.googleAnalyticsService.auth, user);
          this.subscriptions.add(this.akitaCartService.transferAnonymousCartAsync());

          if (user) {
            this.loggedIn(user);
          }
        },
        error: (error: unknown) => {
          const parsed = error as HttpErrorResponse;
          this.failed = true;
          this.loading = false;

          configureScope((scope) => {
            scope.setUser({});
          });

          let errorCode = '';
          if (error && parsed.error && parsed.error.error) {
            errorCode = parsed.error.error;
          }

          if (errorCode === 'USER_NOT_FOUND') {
            if (emailField) {
              emailField.setErrors({ USER_NOT_FOUND: true });
            }
          } else if (errorCode === 'USER_BANNED') {
            if (emailField) {
              emailField.setErrors({ USER_BANNED: true });
            }
          } else if (errorCode === 'USER_LOGIN_INVALID_CREDENTIALS') {
            if (passwordField) {
              passwordField.setErrors({ INVALID_CREDENTIALS: true });
            }
          } else {
            if (passwordField) {
              passwordField.setErrors({ UNKNOWN: true });
            }
          }

          this.changeDetector.markForCheck();
        },
      });
  }

  public onSignWithGoogle(): boolean {
    this.loading = true;
    this.submitted = true;
    this.failed = false;
    this.success = false;
    this.changeDetector.markForCheck();

    this.subscriptions.add(
      this.akitaAuthService
        .signWithGoogle()
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (user: UserModel | null) => {
            logSignupGoogle(this.googleAnalyticsService.auth, user);
            this.submitted = false;
            this.subscriptions.add(this.akitaCartService.transferAnonymousCartAsync());

            if (user) {
              this.loggedIn(user, true);
            }
          },
          error: () => {
            this.passwordFormControl.setErrors({ UNKNOWN: true });
            this.passwordFormControl.markAsTouched();
            this.failed = true;
            this.submitted = false;
            this.loading = false;
            this.changeDetector.markForCheck();
          },
        })
    );
    return false;
  }

  public onSignWithApple(): boolean {
    this.loading = true;
    this.submitted = true;
    this.failed = false;
    this.success = false;
    this.changeDetector.markForCheck();

    this.subscriptions.add(
      this.akitaAuthService
        .signWithApple()
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (user: UserModel | null) => {
            logSignupApple(this.googleAnalyticsService.auth, user);
            this.submitted = false;
            this.subscriptions.add(this.akitaCartService.transferAnonymousCartAsync());

            if (user) {
              this.loggedIn(user, true);
            }
          },
          error: () => {
            this.passwordFormControl.setErrors({ UNKNOWN: true });
            this.passwordFormControl.markAsTouched();
            this.failed = true;
            this.submitted = false;
            this.loading = false;
            this.changeDetector.markForCheck();
          },
        })
    );
    return false;
  }

  public get signUpQueryParams(): any {
    let params = {
      ...this.akitaRouterQuery.queryParamsToKeep,
    };
    const emailField = this.signInForm.get('email');
    if (emailField && emailField.status === 'VALID') {
      params = { ...params, email: emailField.value };
    }
    const username = this.params.username || this.queryParams.username || '';
    if (username) {
      params = { ...params, username: username };
    }
    if (this.redirect) {
      params = { ...params, redirect: encodeURI(this.redirect) };
    }
    return params;
  }

  public authUrlFragment(authType: string): Array<string> {
    const fragment = this.akitaRouterQuery.userUrlFragment;
    fragment.push(authType || 'sign-up');
    return fragment;
  }

  public togglePassword($event: { preventDefault: () => void }): boolean {
    this.hidePassword = !this.hidePassword;
    this.changeDetector.markForCheck();
    $event.preventDefault();
    return false;
  }

  public changeDialogMode(mode?: string | null): boolean {
    const username: string | null =
      this.params.username ||
      this.queryParams.username ||
      this.placeholderUsername ||
      null;
    let email: string | null = null;
    const emailField = this.signInForm.get('email');
    if (emailField && emailField.status === 'VALID') {
      email = emailField.value;
    }

    this.dialogModeChange.emit({
      mode: mode || 'sign-in',
      email: email,
      username: username,
      previousMode: 'auth-intro',
    });

    return false;
  }

  public close(dismiss?: boolean): boolean {
    this.closeDialog.emit(dismiss);
    return false;
  }

  public stopPropagation(): boolean {
    return false;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }

    if (this.urlObserver) {
      this.urlObserver.unsubscribe();
    }

    if (this.userLoginObserver) {
      this.userLoginObserver.unsubscribe();
    }

    if (this.langChangesObserver) {
      this.langChangesObserver.unsubscribe();
    }

    if (this.sha256HasherObserver) {
      this.sha256HasherObserver.unsubscribe();
    }
  }
}
